/**
 * Detail page features: expandable amenities, area expander, nearby amenities dialog
 */

export function initExpandableAmenities() {
    document.querySelectorAll('[data-amenities-max]').forEach(list => {
        const desktopMax = parseInt(list.dataset.amenitiesMax, 10);
        const tabletMax = parseInt(list.dataset.amenitiesTabletMax || desktopMax, 10);
        const mobileMax = parseInt(list.dataset.amenitiesMobileMax || '5', 10);
        const items = [...list.children];
        const expandItem = list.querySelector('[data-amenities-expand]');
        const contentItems = items.filter(li => li !== expandItem);
        const amenityOnly = contentItems.filter(li => !li.classList.contains('amenity-type'));

        function applyVisibility() {
            const w = window.innerWidth;
            const max = w <= 700 ? mobileMax : w <= 1000 ? tabletMax : desktopMax;
            const hasOverflow = contentItems.length > max;
            const visibleMax = hasOverflow ? max - 1 : max;

            contentItems.forEach((item, i) => {
                if (i >= visibleMax) {
                    item.classList.add('amenity-hidden');
                } else {
                    item.classList.remove('amenity-hidden');
                }
            });

            if (!hasOverflow && expandItem) expandItem.hidden = true;

            return { hasOverflow, visibleMax };
        }

        const initial = applyVisibility();
        list.classList.add('amenities-ready');

        if (expandItem) {
            const label = expandItem.querySelector('[data-amenities-expand-label]');

            function updateExpandLabel() {
                const { hasOverflow, visibleMax } = applyVisibility();
                const hiddenCount = contentItems.length - visibleMax;
                if (hasOverflow && hiddenCount > 0) {
                    const isMobile = window.innerWidth <= 700;
                    if (label) label.textContent = isMobile ? `${hiddenCount} more` : `${hiddenCount} more amenities`;
                    expandItem.hidden = false;
                } else {
                    expandItem.hidden = true;
                }
            }

            updateExpandLabel();
            window.addEventListener('resize', updateExpandLabel);

            expandItem.addEventListener('click', () => {
                openAmenitiesDialog(contentItems);
            });
        }
    });
}

function openAmenitiesDialog(items) {
    const dialog = document.getElementById('amenities-dialog');
    if (!dialog) return;

    const dialogList = dialog.querySelector('.amenities-dialog-list');
    if (!dialogList) return;

    dialogList.innerHTML = '';
    items.forEach(item => {
        const clone = item.cloneNode(true);
        clone.classList.remove('amenity-hidden');
        clone.querySelectorAll('[data-tooltip-target]').forEach(btn => btn.remove());
        clone.querySelectorAll('.amenity-check-icon').forEach(svg => svg.remove());
        dialogList.appendChild(clone);
    });

    dialog.showModal();
}

/**
 * Generic text clamp with read-more toggle.
 *
 * Looks for all elements with [data-clamp-text]. Each one should contain:
 *   - A child with class .clamp-inner (the text to clamp)
 *   - A child with [data-clamp-toggle] (the read-more button, initially hidden)
 *
 * Behaviour:
 *   - Multiple block children (paragraphs): shows only the first, hides the rest
 *   - Single child or no block children: uses CSS line-clamp as fallback
 */
function initTextClamp() {
    document.querySelectorAll('[data-clamp-text]').forEach(container => {
        const inner = container.querySelector('.clamp-inner');
        const toggle = container.querySelector('[data-clamp-toggle]');
        if (!inner || !toggle) return;

        // Count block-level children (p, div, ul, ol, blockquote, etc.)
        const blockChildren = [...inner.children].filter(el => {
            const display = getComputedStyle(el).display;
            return display === 'block' || display === 'list-item';
        });

        const hasMultipleBlocks = blockChildren.length > 1;

        if (hasMultipleBlocks) {
            // Multi-paragraph mode: hide all children after the first
            // but only if later blocks have actual content
            const hideable = blockChildren.filter((child, i) =>
                i > 0 && child.textContent.trim() !== ''
            );

            if (hideable.length === 0) {
                // Nothing meaningful to hide — skip clamping entirely
                toggle.hidden = true;
                return;
            }

            blockChildren.forEach((child, i) => {
                if (i > 0) child.classList.add('clamp-hidden');
            });
            inner.classList.add('is-paragraph-clamped');
            toggle.hidden = false;
        } else {
            // Single paragraph mode: measure natural height before clamping
            const naturalHeight = inner.scrollHeight;
            inner.classList.add('is-line-clamped');
            inner.offsetHeight; // force reflow
            const clampedHeight = inner.clientHeight;

            if (naturalHeight <= clampedHeight + 2) {
                // Content fits within the clamp — no need for read more
                inner.classList.remove('is-line-clamped');
                toggle.hidden = true;
                return;
            }

            toggle.hidden = false;
        }

        let expanded = false;

        toggle.addEventListener('click', (e) => {
            e.preventDefault();

            if (hasMultipleBlocks) {
                blockChildren.forEach((child, i) => {
                    if (i > 0) child.classList.remove('clamp-hidden');
                });
            } else {
                inner.classList.add('is-expanded');
            }

            toggle.remove();
        });
    });
}

export function initAreaExpander() {
    initTextClamp();
}

export function initGuideIntro() {
    // Wrap guide profile H2s in .section-tab-header to match detail page styling
    document.querySelectorAll('.guide-profile-content h2').forEach(h2 => {
        const wrapper = document.createElement('div');
        wrapper.className = 'section-tab-header';
        h2.classList.add('section-tab');
        h2.parentNode.insertBefore(wrapper, h2);
        wrapper.appendChild(h2);
    });
}

export function initNearbyAmenities() {
    const seeMoreBtn = document.querySelector('[data-amenities-see-more]');
    if (!seeMoreBtn) return;

    seeMoreBtn.addEventListener('click', () => {
        openNearbyAmenitiesDialog();
    });
}

export function initTransportDistanceParsing() {
    const iconMap = {
        airport: 'plane-departure',
        railway: 'train-track',
        underground: 'train-tunnel',
        bus: 'bus',
        ferry: 'ferry',
        roads: 'car-bus',
        tram: 'train-tram'
    };

    document.querySelectorAll('.transport-card').forEach(card => {
        const type = card.dataset.transportType;
        const spriteId = iconMap[type];
        if (spriteId) {
            const useEl = card.querySelector('.transport-icon use');
            if (useEl) {
                const href = useEl.getAttribute('href') || useEl.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
                if (href) {
                    const base = href.split('#')[0];
                    useEl.setAttribute('href', base + '#' + spriteId);
                }
            }
        }
    });

    // ============================================================
    // OLD FORMAT BACKWARD COMPAT — START
    // Parses old-style descriptions like "Airport Name (typically 25 min, 29.5 km via road)"
    // by splitting at the first "(" to create a separate .transport-distance element.
    // New format entries already have .transport-distance in the HTML (from the distance
    // grid column) and won't have "(" in the name, so this block safely skips them.
    //
    // MIGRATION NOTE: Once ALL entries have been updated to the new transport format
    // (distance/latitude/longitude columns populated in the transport grid), this entire
    // block from "OLD FORMAT BACKWARD COMPAT — START" to "— END" can be safely removed.
    // ============================================================
    document.querySelectorAll('.transport-name').forEach(nameEl => {
        // Skip roads cards — parentheses are part of road names, not distance info
        const card = nameEl.closest('.transport-card');
        if (card && card.dataset.transportType === 'roads') return;

        const text = nameEl.textContent.trim();
        const parenIndex = text.indexOf('(');
        if (parenIndex === -1) return;

        const name = text.substring(0, parenIndex).trim();
        const distance = text.substring(parenIndex + 1).replace(/\)$/, '').trim();

        nameEl.textContent = name;

        const distEl = document.createElement('div');
        distEl.className = 'transport-distance';
        distEl.textContent = distance;

        // Insert after the primary row (not inside it) so distance sits on its own line
        const primaryRow = nameEl.closest('.transport-primary-row');
        if (primaryRow) {
            primaryRow.after(distEl);
        } else {
            nameEl.after(distEl);
        }
    });

    // Also handle old format in sub-list items
    document.querySelectorAll('.transport-sub-name').forEach(nameEl => {
        const text = nameEl.textContent.trim();
        const parenIndex = text.indexOf('(');
        if (parenIndex === -1) return;

        const name = text.substring(0, parenIndex).trim();
        const distance = text.substring(parenIndex + 1).replace(/\)$/, '').trim();

        nameEl.textContent = name;

        const distEl = nameEl.parentElement.querySelector('.transport-sub-distance');
        if (distEl && !distEl.textContent.trim()) {
            distEl.textContent = distance;
        }
    });
    // OLD FORMAT BACKWARD COMPAT — END
}

function openNearbyAmenitiesDialog() {
    const dialog = document.getElementById('nearby-amenities-dialog');
    if (!dialog) return;

    const dialogGrid = dialog.querySelector('.nearby-amenities-grid-dialog');
    if (!dialogGrid) return;

    const sourceGrid = document.querySelector('.nearby-amenities .nearby-amenities-grid');
    if (!sourceGrid) return;
    const allCategories = [...sourceGrid.querySelectorAll('.nearby-category')];

    dialogGrid.innerHTML = '';
    allCategories.forEach(category => {
        const clone = category.cloneNode(true);
        clone.classList.remove('nearby-category-hidden');
        clone.querySelectorAll('[data-full-name]').forEach(el => {
            el.textContent = el.dataset.fullName;
        });
        dialogGrid.appendChild(clone);
    });

    dialog.showModal();
}
